// app/ has no slice, so no model/ to hold hooks — colocate the hook next to
// the component per spec §4.3 (docs/superpowers/specs/2026-07-30-separate-ui-logic-design.md).
import { handleMonitoringFrontendError } from '@/shared/api';
import { EMITTER_CONSTANTS, ENV_ENUM, ENV } from '@/shared/config';
import { generateToken, auth } from '@/shared/lib';
import { useEmitter } from '@/shared/lib/customHooks';
import type {
  AddSignatureTimeTrackingEmitterMessage,
  SocketConnectionErrorMessage,
} from '@/shared/lib';
import { initializeGtm, isAllowedGtm } from '@/shared/lib/integrations';
import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import { FRONT_END_ERRORS, TRACKING_SOCKET_ERRORS } from './frontEndMonitoring.constants';
import type { FrontEndMonitoringProps } from './frontEndMonitoring.types';

/**
 * Wires up front-end monitoring for the portal once the login status has been checked. On that flag it probes the CDN font to detect a blocked domain, initializes Sentry and, unless the session came from Mercy, Google Tag Manager, and it subscribes emitter handlers that report socket connection errors and signature time tracking.
 *
 * @param hasCheckedLoginStatus - Whether the login status check has completed, gating the monitoring setup.
 * @returns Nothing.
 */
export const useFrontEndMonitoring = ({ hasCheckedLoginStatus }: FrontEndMonitoringProps) => {
  const loginFrom = auth.getLoginFrom();

  /**
   * Detects whether the CDN-hosted Cera Pro font is reachable, which reveals a blocked domain. Fetches the font URL and throws when the response is not ok, then reports the caught error name, message, status and URL as a domain-blocked front-end error for the current user.
   *
   * @returns A promise resolving once the font has been fetched and any blocked-domain error reported.
   */
  const handleCheckLoadFontStatus = async () => {
    const fontUrl = 'https://public.cdn.biotricity.com/font/ceraPro/Cera-Pro-Regular.ttf';
    try {
      const response = await fetch(fontUrl);
      if (!response.ok) {
        const error = new Error('Failed to load font due to CORS or other issue');
        (error as any).status = response.status;
        throw error;
      }
    } catch (error) {
      console.error('Failed to check load font status: ', error);
      const { name, message, status } = error as {
        name?: string;
        message?: string;
        status?: number;
      };
      handleMonitoringFrontendError({
        exception: JSON.stringify({
          name,
          message,
          status,
          url: fontUrl,
        }),
        type: FRONT_END_ERRORS.DOMAIN_BLOCKED,
        userId: auth.userId(),
      });
    }
  };

  /**
   * Starts Sentry error reporting, but only on the customer environment. Initializes the client with tracing and session tracking disabled and a pass-through beforeSend hook, then attaches the current user id, username and email as context.
   *
   * @returns Nothing.
   */
  const handleInitiateSentry = () => {
    if (ENV === ENV_ENUM.customer) {
      Sentry.init({
        dsn: 'https://b4baf05d1d39828736321f64a021302b@o4509592128913408.ingest.us.sentry.io/4509592131534848',
        tracesSampleRate: 0,
        autoSessionTracking: false,
        integrations: [],
        /**
         * Sentry hook deciding what is reported for each captured event. Returns the event unchanged, so no filtering or scrubbing is applied before it is sent.
         *
         * @param event - Event Sentry is about to send.
         * @param _hint - Unused capture hint from Sentry.
         * @returns The event, unmodified.
         */
        beforeSend(event, _hint) {
          return event;
        },
      });
      Sentry.setContext('userInfo', {
        userId: auth.userId(),
        username: auth.username(),
        email: auth.email(),
      });
    }
  };

  /**
   * Boots Google Tag Manager when analytics tracking is permitted. Calls the GTM initializer only if the allowed-GTM check passes.
   *
   * @returns Nothing.
   */
  const handleInitiateGtm = () => {
    if (isAllowedGtm()) {
      initializeGtm();
    }
  };

  /**
   * Reports socket connection failures that are on the tracked list to Sentry and the backend. Untracked messages are ignored; otherwise it generates an access token, attaches the error name, message, token and socket id as Sentry context, captures the exception, and posts the same payload as a socket-connection-error front-end error.
   *
   * @param msg - Emitted socket error carrying its name, message, socketId and the error object.
   * @returns A promise resolving once the connection error has been captured and posted.
   */
  const handleSocketConnectionErrorEmitter = async (msg: SocketConnectionErrorMessage) => {
    if (TRACKING_SOCKET_ERRORS.includes(msg.message)) {
      const accessToken = await generateToken();
      Sentry.setContext('errorType', {
        name: msg.name,
        message: msg.message,
        accessToken,
        socketId: msg.socketId,
      });
      Sentry.captureException(msg.error);
      await handleMonitoringFrontendError({
        exception: JSON.stringify({
          name: msg.name,
          message: msg.message,
          accessToken,
          socketId: msg.socketId,
        }),
        type: FRONT_END_ERRORS.SOCKET_CONNECTION_ERROR,
        userId: auth.userId(),
      });
    }
  };

  /**
   * Records how long adding a report signature took so slow signing can be traced. Generates an access token and posts it together with the tracking duration, study fid, type, new-format flag, report id and event fid as an add-signature-time-tracking front-end error for the current user.
   *
   * @param msg - Emitted payload carrying the signature tracking time and the study, report and event identifiers.
   * @returns A promise resolving once the signature timing has been posted.
   */
  const handleAddSignatureTimeTrackingEmitter = async (
    msg: AddSignatureTimeTrackingEmitterMessage,
  ) => {
    const accessToken = await generateToken();
    await handleMonitoringFrontendError({
      exception: JSON.stringify({
        accessToken,
        addSignatureTrackingTime: msg.addSignatureTrackingTime,
        studyFid: msg.studyFid,
        type: msg.type,
        isNewFormat: msg.isNewFormat,
        reportId: msg.reportId,
        eventFid: msg.eventFid,
      }),
      type: FRONT_END_ERRORS.ADD_SIGNATURE_TIME_TRACKING,
      userId: auth.userId(),
    });
  };

  useEffect(() => {
    if (hasCheckedLoginStatus) {
      handleCheckLoadFontStatus();
      handleInitiateSentry();

      if (loginFrom !== 'Mercy') {
        handleInitiateGtm();
      }
    }
  }, [hasCheckedLoginStatus]);

  useEmitter(EMITTER_CONSTANTS.SOCKET_CONNECTION_ERROR, handleSocketConnectionErrorEmitter, []);

  useEmitter(
    EMITTER_CONSTANTS.ADD_SIGNATURE_TIME_TRACKING,
    handleAddSignatureTimeTrackingEmitter,
    [],
  );
};
